// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import baseTheme, { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContext from "@insite/mobius/GridContainer/GridContext";
import { GridItemStyle } from "@insite/mobius/GridItem/GridItem";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import * as React from "react";
import { useContext, useEffect, useState } from "react";
import styled, { css, ThemeContext } from "styled-components";

export type GridContainerProps = MobiusStyledComponentProps<
    "div",
    {
        /** CSS string or styled-components function to be injected into this component. */
        css?: StyledProp<GridContainerProps>;
        /** The amount of space between grid items, in pixels. */
        gap?: number;
        /** Props to be passed to grid offset. */
        offsetProps?: MobiusStyledComponentProps<"div">;
        /** CSS string or styled-components function to be injected into this component. */
        offsetCss?: StyledProp<GridContainerProps>;
    }
>;

const GridOffset = styled.div<{ gap: number }>`
    margin: ${({ gap }) => -gap / 2}px;
    ${injectCss}
`;

const GridWrapper = styled.div<GridContainerProps>`
    flex-grow: 1;
    width: 100%;
    ${({ theme }: { theme: BaseTheme }) => {
        const { maxWidths } = (theme as any).breakpoints || (baseTheme as any).breakpoints;
        return breakpointMediaQueries(
            theme,
            maxWidths.map((mw: any) =>
                mw
                    ? css`
                          max-width: ${mw}px;
                      `
                    : null,
            ),
        );
    }}

    @media print {
        max-width: 100%;
    }
    > ${GridOffset} {
        display: flex;
        flex-wrap: wrap;
        width: ${({ gap }) => ((gap as number) > 0 ? `calc(100% + ${gap}px)` : "100%")};

        @media print {
            display: block;
        }
    }
    ${GridItemStyle} {
        box-sizing: border-box;
        margin: 0;
        flex-grow: 0;
        flex-shrink: 1;

        @media print {
            display: inline-block;
            margin-right: -0.22rem;
            vertical-align: top;
        }
    }
    ${injectCss}
`;

const isMaxScreenSize = (value: number) => window && window.matchMedia(`(max-width: ${value}px)`).matches;

/**
 * GridContainer provides a 12-column grid scaffolding for GridItem components.
 */
const GridContainer: React.FC<GridContainerProps> = ({
    children,
    css,
    offsetCss,
    offsetProps,
    gap: propGap = 30,
    ...otherProps
}) => {
    const [computedGap, setComputedGap] = useState(propGap);
    const theme = useContext(ThemeContext);

    // <XNG-Changes>
    const gapCalculation = () => {
        const { maxWidths, paddingValues } = theme.breakpoints || baseTheme.breakpoints;
        const maxWidth = maxWidths[maxWidths.length - 1];
        const padding = paddingValues ? paddingValues[paddingValues.length - 1] : 20;
        const paddingsSum = 2 * padding;
        if (isMaxScreenSize(maxWidth + propGap) && propGap > paddingsSum) {
            setComputedGap(paddingsSum);
        } else {
            setComputedGap(propGap);
        }
    };

    useEffect(() => {
        // to handle gap when window resize
        window.addEventListener("resize", gapCalculation, { passive: true });
        gapCalculation();
        return () => window.removeEventListener("resize", gapCalculation);
    }, []);
    // </XNG-Changes>

    if (!React.Children.count(children)) {
        return null;
    }

    return (
        <GridWrapper css={css} {...otherProps} gap={computedGap ?? 30}>
            <GridContext.Provider value={{ gap: computedGap ?? 30 }}>
                <GridOffset {...offsetProps} css={offsetCss} gap={computedGap ?? 30}>
                    {children}
                </GridOffset>
            </GridContext.Provider>
        </GridWrapper>
    );
};

/** @component */
export default GridContainer;

export { GridOffset, GridWrapper };

